(() => {
  const SECTIONS_READY_EVENT = 'sb:product-thumbnail-sections:ready';
  const DECLARATIVE_ROOT_SELECTOR = '[data-sb-thumbnail-section]';
  const QUICK_ADD_DESKTOP_QUERY = '(min-width: 750px)';

  const getMatchingRoots = (scope, selector) => {
    if (!(scope instanceof Element || scope instanceof Document) || !selector) return [];
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope instanceof Element && scope.matches(selector)) {
      roots.unshift(scope);
    }
    return roots;
  };

  const whenThumbnailEngineReady = (callback) => {
    if (
      window.SBProductThumbnail &&
      typeof window.SBProductThumbnail.initThumbnailMediaScope === 'function'
    ) {
      callback();
      return;
    }

    document.addEventListener('sb:product-thumbnail:ready', callback, { once: true });
  };

  const ensureThumbnailMediaScope = (scope = document) => {
    whenThumbnailEngineReady(() => {
      if (
        window.SBProductThumbnail &&
        typeof window.SBProductThumbnail.initThumbnailMediaScope === 'function'
      ) {
        window.SBProductThumbnail.initThumbnailMediaScope(scope, {
          initSwatchInteractions: true,
        });
      }
    });
  };

  const getShopRoot = () => {
    const rootPath =
      window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
        ? window.Shopify.routes.root
        : '/';
    return rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
  };

  const addToCartNative = async (
    variantId,
    quantity = 1,
    {
      source = 'product-thumbnail',
      sourceIdPrefix = 'product-thumbnail',
    } = {}
  ) => {
    const parsedVariantId = Number.parseInt(`${variantId}`, 10);
    const parsedQuantity = Number.parseInt(`${quantity}`, 10);
    if (!Number.isFinite(parsedVariantId) || parsedVariantId <= 0) {
      throw new Error('Invalid variant id');
    }
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      throw new Error('Invalid quantity');
    }

    const shopRoot = getShopRoot();
    const cartUrl = (path) => `${shopRoot}${path}`;
    const formData = new FormData();
    formData.append('id', `${parsedVariantId}`);
    formData.append('quantity', `${parsedQuantity}`);

    const addResponse = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });
    if (!addResponse.ok) {
      throw new Error('Add to cart failed');
    }

    const cartResponse = await fetch(cartUrl('cart.js'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cartResponse.ok) {
      throw new Error('Cart refresh failed');
    }
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: `${sourceIdPrefix}-${parsedVariantId}`,
          data: {
            source,
            itemCount: parsedQuantity,
            variantId: parsedVariantId,
          },
        },
      })
    );

    return cart;
  };

  const shouldResetAddedClass = (button, mode) => {
    if (mode === 'none') return false;
    if (mode === 'small-only') {
      return button.classList.contains('sb-product-thumbnail__add-to-cart');
    }
    return true;
  };

  const canUseQuickAddModal = () =>
    typeof window.matchMedia === 'function' && window.matchMedia(QUICK_ADD_DESKTOP_QUERY).matches;

  const syncAddToCartLabelWidth = (button) => {
    if (!(button instanceof HTMLElement)) return;
    const label = button.querySelector('[data-add-to-cart-label]');
    if (!(label instanceof HTMLElement)) return;

    const labelWidth = Math.ceil(label.scrollWidth);
    if (labelWidth <= 0) return;
    button.style.setProperty('--sb-add-label-width', `${labelWidth + 2}px`);
  };

  const bindAddToCartLabelWidths = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    scope.querySelectorAll('.sb-product-thumbnail__add-to-cart').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      syncAddToCartLabelWidth(button);
      window.requestAnimationFrame(() => syncAddToCartLabelWidth(button));

      const label = button.querySelector('[data-add-to-cart-label]');
      if (!(label instanceof HTMLElement) || label.dataset.sbAddLabelWidthBound === 'true') return;
      label.dataset.sbAddLabelWidthBound = 'true';

      const observer = new MutationObserver(() => syncAddToCartLabelWidth(button));
      observer.observe(label, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    });

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => {
        scope.querySelectorAll('.sb-product-thumbnail__add-to-cart').forEach((button) => {
          syncAddToCartLabelWidth(button);
        });
      }).catch(() => {});
    }
  };

  const quickAddProductContentCache = new Map();
  const quickAddRequests = new WeakMap();
  const quickAddKeyboardTriggerByModal = new WeakMap();
  const quickAddPointerTriggerByModal = new WeakMap();
  let quickAddProductScriptPromise = null;
  let quickAddMediaGalleryScriptPromise = null;

  // This head-deferred initializer runs before the page's product scripts.
  // DOMContentLoaded settles those parser-owned scripts, including failures.
  const pageScriptsReady = document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));

  const setQuickAddModalOpenState = (isOpen) => {
    document.documentElement.classList.toggle('sb-quick-add-modal-open', isOpen);
  };

  const getTextHash = (text) => {
    let hash = 0;
    for (let index = 0; index < text.length; index += 1) {
      hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
    }
    return hash.toString(36);
  };

  const closeQuickAddModal = (modal) => {
    if (!(modal instanceof HTMLDialogElement) || !modal.open) return;
    quickAddRequests.get(modal)?.abort();
    quickAddRequests.delete(modal);
    delete modal.dataset.quickAddLoading;
    modal.querySelector('[data-quick-add-content]')?.removeAttribute('aria-busy');
    modal.close();
    setQuickAddModalOpenState(Boolean(document.querySelector('.sb-product-thumbnail__quick-add-modal[open]')));
    const keyboardTrigger = quickAddKeyboardTriggerByModal.get(modal);
    const pointerTrigger = quickAddPointerTriggerByModal.get(modal);
    quickAddKeyboardTriggerByModal.delete(modal);
    quickAddPointerTriggerByModal.delete(modal);
    modal.removeAttribute('data-pointer-open');

    if (pointerTrigger instanceof HTMLElement && document.activeElement === pointerTrigger) {
      pointerTrigger.blur();
    }
    if (keyboardTrigger instanceof HTMLElement && document.contains(keyboardTrigger)) {
      window.requestAnimationFrame(() => {
        if (!modal.open && document.contains(keyboardTrigger)) {
          keyboardTrigger.focus({ preventScroll: true });
        }
      });
    }
  };

  const closeAllQuickAddModals = () => {
    document.querySelectorAll('.sb-product-thumbnail__quick-add-modal[open]').forEach((modal) => {
      closeQuickAddModal(modal);
    });
  };

  const getOpenQuickAddModal = () => document.querySelector('.sb-product-thumbnail__quick-add-modal[open]');

  const getQuickAddProductUrl = (thumbnail, trigger) => {
    const rawUrl =
      thumbnail?.dataset.thumbnailBaseUrl ||
      thumbnail?.querySelector('[data-thumbnail-link]')?.getAttribute('href') ||
      '';
    if (!rawUrl || rawUrl === '#') return '';
    const url = new URL(rawUrl, window.location.origin);
    const variantId =
      trigger?.dataset.variantId ||
      thumbnail?.dataset.thumbnailVariantId ||
      '';
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  };

  const makeQuickAddRatingStatic = (productRoot) => {
    if (!(productRoot instanceof HTMLElement)) return;

    productRoot
      .querySelectorAll('a.sb-review-stars__stars, a.sb-review-stars__count')
      .forEach((ratingLink) => {
        const staticRating = document.createElement('span');
        staticRating.className = ratingLink.className;
        const ariaLabel = ratingLink.getAttribute('aria-label');
        if (ariaLabel) staticRating.setAttribute('aria-label', ariaLabel);
        staticRating.append(...Array.from(ratingLink.childNodes));
        ratingLink.replaceWith(staticRating);
      });
  };

  const getQuickAddMediaSkeletonCount = (modal) => {
    const rawCount = Number.parseInt(modal?.dataset.quickAddMediaCount || '', 10);
    if (!Number.isFinite(rawCount) || rawCount < 1) return 1;
    return Math.min(rawCount, 12);
  };

  const renderQuickAddLoadingGallery = (modal, content) => {
    if (!(modal instanceof HTMLElement) || !(content instanceof HTMLElement)) return;
    const mediaCount = getQuickAddMediaSkeletonCount(modal);
    const gallery = document.createElement('div');
    gallery.className = 'sb-product-thumbnail__quick-add-loading-gallery';
    gallery.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < mediaCount; index += 1) {
      const item = document.createElement('span');
      item.className = 'sb-product-thumbnail__quick-add-loading-media';
      gallery.appendChild(item);
    }

    window.SBProductMain?.destroyProductScope?.(content);
    content.replaceChildren(gallery);
  };

  const ensureQuickAddStylesheet = (href) => {
    const normalizedHref = new URL(href, window.location.href).href;
    const existingStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).find(
      (stylesheet) => stylesheet.href === normalizedHref
    );
    if (existingStylesheet?.sheet) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const stylesheet = existingStylesheet || document.createElement('link');
      const listeners = new AbortController();
      stylesheet.addEventListener('load', () => {
        listeners.abort();
        resolve();
      }, { signal: listeners.signal });
      stylesheet.addEventListener('error', () => {
        listeners.abort();
        stylesheet.remove();
        reject(new Error(`Quick add stylesheet failed: ${normalizedHref}`));
      }, { signal: listeners.signal });
      if (!existingStylesheet) {
        stylesheet.rel = 'stylesheet';
        stylesheet.href = normalizedHref;
        document.head.appendChild(stylesheet);
      }
    });
  };

  const ensureQuickAddScript = async (src, existingPromise, failureMessage, isReady) => {
    if (existingPromise) return existingPromise;

    const normalizedSrc = new URL(src, window.location.href).href;
    const existingScript = Array.from(document.querySelectorAll('script[src]')).find(
      (script) => script.src === normalizedSrc
    );
    if (existingScript) {
      await pageScriptsReady;
      if (isReady()) return;
      existingScript.remove();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = normalizedSrc;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => {
        script.remove();
        reject(new Error(failureMessage));
      };
      document.body.appendChild(script);
    });
  };

  const ensureQuickAddProductAssets = async (sectionWrapper, sourceDocument) => {
    if (!(sectionWrapper instanceof Element)) return;
    if (!(sourceDocument instanceof Document)) return;

    const requiredStylesheets = Array.from(
      sourceDocument.querySelectorAll('link[rel="stylesheet"][href]')
    ).filter((stylesheet) => {
      const href = stylesheet.getAttribute('href') || '';
      return href.includes('product-main.css') || href.includes('product-media-gallery-modal.css');
    });
    await Promise.all(
      requiredStylesheets.map((stylesheet) =>
        ensureQuickAddStylesheet(stylesheet.getAttribute('href') || '')
      )
    );

    sectionWrapper.querySelectorAll('style').forEach((style) => {
      const styleText = style.textContent || '';
      if (!styleText.trim()) return;
      const styleId = style.id || `sb-quick-add-style-${getTextHash(styleText)}`;
      if (document.getElementById(styleId)) return;
      const nextStyle = document.createElement('style');
      nextStyle.id = styleId;
      nextStyle.textContent = styleText;
      document.head.appendChild(nextStyle);
    });

    if (
      !window.SBProductMain ||
      typeof window.SBProductMain.initProductScope !== 'function'
    ) {
      const productScript = Array.from(sourceDocument.querySelectorAll('script[src]')).find((script) => {
        const src = script.getAttribute('src') || '';
        return src.includes('product-main.js');
      });
      if (!productScript) throw new Error('Quick add product script missing');

      quickAddProductScriptPromise = ensureQuickAddScript(
        productScript.getAttribute('src') || '',
        quickAddProductScriptPromise,
        'Quick add product script failed',
        () => typeof window.SBProductMain?.initProductScope === 'function'
      ).catch((error) => {
        quickAddProductScriptPromise = null;
        throw error;
      });
      await quickAddProductScriptPromise;
    }

    if (
      !window.SBProductMediaGalleryModal ||
      typeof window.SBProductMediaGalleryModal.init !== 'function'
    ) {
      const mediaGalleryScript = Array.from(sourceDocument.querySelectorAll('script[src]')).find((script) => {
        const src = script.getAttribute('src') || '';
        return src.includes('product-media-gallery-modal.js');
      });
      if (!mediaGalleryScript) throw new Error('Quick add media gallery script missing');

      quickAddMediaGalleryScriptPromise = ensureQuickAddScript(
        mediaGalleryScript.getAttribute('src') || '',
        quickAddMediaGalleryScriptPromise,
        'Quick add media gallery script failed',
        () => typeof window.SBProductMediaGalleryModal?.init === 'function'
      ).catch((error) => {
        quickAddMediaGalleryScriptPromise = null;
        throw error;
      });
      await quickAddMediaGalleryScriptPromise;
    }
  };

  const loadQuickAddProductContent = async (trigger, modal) => {
    const thumbnail = trigger.closest('.sb-product-thumbnail');
    const content = modal.querySelector('[data-quick-add-content]');
    if (!(thumbnail instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    const productUrl = getQuickAddProductUrl(thumbnail, trigger);
    if (!productUrl) return;

    const request = new AbortController();
    quickAddRequests.set(modal, request);
    modal.dataset.quickAddLoading = 'true';
    content.setAttribute('aria-busy', 'true');
    renderQuickAddLoadingGallery(modal, content);

    try {
      let cachedMarkup = quickAddProductContentCache.get(productUrl);
      if (!cachedMarkup) {
        const response = await fetch(productUrl, {
          signal: request.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Quick add product fetch failed');

        const html = await response.text();
        if (request.signal.aborted || !modal.isConnected) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const productRoot = documentFragment.querySelector('[data-product-grid-content]');
        const sectionWrapper = productRoot?.closest('.shopify-section') || productRoot?.parentElement;
        if (!(productRoot instanceof HTMLElement) || !(sectionWrapper instanceof Element)) {
          throw new Error('Quick add product content missing');
        }

        await ensureQuickAddProductAssets(sectionWrapper, documentFragment);
        if (request.signal.aborted || !modal.isConnected) return;
        productRoot.querySelectorAll('.sb-product-main__sticky-bottom-bar-wrap').forEach((stickyBar) => {
          stickyBar.remove();
        });
        cachedMarkup = productRoot.outerHTML;
        quickAddProductContentCache.set(productUrl, cachedMarkup);
      }

      const template = document.createElement('template');
      template.innerHTML = cachedMarkup.trim();
      const productRoot = template.content.querySelector('.sb-product-main');
      if (productRoot instanceof HTMLElement) {
        productRoot.classList.add('sb-product-main--quick-add');
        makeQuickAddRatingStatic(productRoot);
        const firstMediaImage = productRoot.querySelector(
          '[data-product-media-grid] img.sb-product-main__media-asset, [data-product-media-grid] img.sb-product-main__media-video-poster'
        );
        if (firstMediaImage instanceof HTMLImageElement) {
          firstMediaImage.loading = 'eager';
          if ('fetchPriority' in firstMediaImage) firstMediaImage.fetchPriority = 'high';
        }
      }

      // Buying controls are ready once their markup, styles and controller are loaded.
      // Gallery images and review content can finish independently.
      window.SBProductMain?.destroyProductScope?.(content);
      content.replaceChildren(...Array.from(template.content.children));

      if (
        window.SBProductMain &&
        typeof window.SBProductMain.initProductScope === 'function'
      ) {
        window.SBProductMain.initProductScope(content);
      }
      if (
        window.SBProductMediaGalleryModal &&
        typeof window.SBProductMediaGalleryModal.init === 'function'
      ) {
        window.SBProductMediaGalleryModal.init(content);
      }
    } catch (_) {
      if (!request.signal.aborted && modal.isConnected) closeQuickAddModal(modal);
    } finally {
      if (quickAddRequests.get(modal) === request) {
        quickAddRequests.delete(modal);
        delete modal.dataset.quickAddLoading;
        content.removeAttribute('aria-busy');
      }
    }
  };

  const openQuickAddModal = (trigger, { openedByKeyboard = false } = {}) => {
    if (!(trigger instanceof HTMLElement) || !canUseQuickAddModal(trigger)) return;
    const thumbnail = trigger.closest('.sb-product-thumbnail');
    const modal = thumbnail?.querySelector('.sb-product-thumbnail__quick-add-modal');
    if (!(modal instanceof HTMLDialogElement)) return;

    closeAllQuickAddModals();
    if (openedByKeyboard) {
      quickAddKeyboardTriggerByModal.set(modal, trigger);
      quickAddPointerTriggerByModal.delete(modal);
      modal.removeAttribute('data-pointer-open');
    } else {
      quickAddPointerTriggerByModal.set(modal, trigger);
      quickAddKeyboardTriggerByModal.delete(modal);
      modal.setAttribute('data-pointer-open', '');
    }
    modal.showModal();
    setQuickAddModalOpenState(true);
    const closeButton = modal.querySelector('[data-quick-add-close]');
    if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
    loadQuickAddProductContent(trigger, modal);
  };

  const bindQuickAddModalTriggers = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    scope.querySelectorAll('[data-quick-add-trigger]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.dataset.sbQuickAddTriggerBound === 'true') return;
      button.dataset.sbQuickAddTriggerBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openQuickAddModal(button, { openedByKeyboard: event.detail === 0 });
      });
    });

    scope.querySelectorAll('.sb-product-thumbnail__quick-add-modal').forEach((modal) => {
      if (!(modal instanceof HTMLDialogElement)) return;
      if (modal.dataset.sbQuickAddModalBound === 'true') return;
      modal.dataset.sbQuickAddModalBound = 'true';

      modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeQuickAddModal(modal);
      });

      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeQuickAddModal(modal);
      });

      modal.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        closeQuickAddModal(modal);
      });

      modal.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          const action = `${form.getAttribute('action') || ''}`.trim();
          if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(action)) return;
          closeQuickAddModal(modal);
        },
        true
      );

      modal.querySelectorAll('[data-quick-add-close]').forEach((closeButton) => {
        closeButton.addEventListener('click', () => closeQuickAddModal(modal));
      });
    });

    if (document.documentElement.dataset.sbQuickAddOutsideClickBound !== 'true') {
      document.documentElement.dataset.sbQuickAddOutsideClickBound = 'true';
      document.addEventListener(
        'click',
        (event) => {
          const openModal = getOpenQuickAddModal();
          if (!(openModal instanceof HTMLDialogElement)) return;
          if (!(event.target instanceof Element)) return;
          if (event.target.closest('[data-quick-add-surface], [data-quick-add-trigger]')) return;
          event.preventDefault();
          event.stopPropagation();
          closeQuickAddModal(openModal);
        },
        true
      );

      document.addEventListener(
        'wheel',
        (event) => {
          const openModal = getOpenQuickAddModal();
          if (!(openModal instanceof HTMLDialogElement)) return;
          if (!(event.target instanceof Element)) return;

          const scroller = event.target.closest('[data-quick-add-content]');
          if (scroller instanceof HTMLElement) {
            const isScrollingUp = event.deltaY < 0;
            const isScrollingDown = event.deltaY > 0;
            const scrollTop = scroller.scrollTop;
            const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
            const isAtTop = scrollTop <= 0;
            const isAtBottom = scrollTop >= maxScrollTop - 1;
            if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) return;
          }

          event.preventDefault();
        },
        { capture: true, passive: false }
      );
    }
  };

  const bindAddToCartButtons = (scope = document, options = {}) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    const {
      boundFlag = 'sbThumbnailAtcBound',
      source = 'product-thumbnail',
      sourceIdPrefix = 'product-thumbnail',
      resetAddedClassMode = 'small-only',
    } = options;

    scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.dataset[boundFlag] === 'true') return;
      button.dataset[boundFlag] = 'true';

      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (button instanceof HTMLButtonElement && button.disabled) return;

        const variantId = Number.parseInt(button.dataset.variantId || '', 10);
        if (!Number.isFinite(variantId) || variantId <= 0) return;

        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
        }
        button.classList.add('is-loading');

        try {
          const usesCartSystem = Boolean(window.SBCart && typeof window.SBCart.add === 'function');
          if (usesCartSystem) {
            await window.SBCart.add(variantId, {
              quantity: 1,
              sourceElement: button,
            });
          } else {
            await addToCartNative(variantId, 1, {
              source,
              sourceIdPrefix,
            });
          }

          if (!usesCartSystem && shouldResetAddedClass(button, resetAddedClassMode)) {
            button.classList.remove('is-added');
          }
        } catch (_) {
          button.classList.remove('is-added');
        } finally {
          button.classList.remove('is-loading');
          if (button instanceof HTMLButtonElement) {
            button.disabled = false;
          }
        }
      });
    });
  };

  const initRoot = (root, options = {}) => {
    if (!(root instanceof HTMLElement)) return;

    const {
      rootInitializedDataKey = 'sbThumbnailSectionInitialized',
      initMedia = true,
      bindAddToCart = true,
      addToCartBoundFlag = 'sbThumbnailAtcBound',
      addToCartSource = 'product-thumbnail',
      addToCartSourceIdPrefix = 'product-thumbnail',
      resetAddedClassMode = 'small-only',
    } = options;

    if (root.dataset[rootInitializedDataKey] === 'true') return;
    root.dataset[rootInitializedDataKey] = 'true';

    if (initMedia) {
      ensureThumbnailMediaScope(root);
    }

    bindAddToCartLabelWidths(root);

    if (bindAddToCart) {
      bindQuickAddModalTriggers(root);
      bindAddToCartButtons(root, {
        boundFlag: addToCartBoundFlag,
        source: addToCartSource,
        sourceIdPrefix: addToCartSourceIdPrefix,
        resetAddedClassMode,
      });
    }
  };

  const initSection = (scope = document, options = {}) => {
    const {
      rootSelector,
      ...rootOptions
    } = options;

    if (!rootSelector) return;
    getMatchingRoots(scope, rootSelector).forEach((root) => {
      initRoot(root, rootOptions);
    });
  };

  const parseBooleanDataAttribute = (value, fallback) => {
    if (typeof value !== 'string' || value.trim() === '') return fallback;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off')
      return false;
    return fallback;
  };

  const parseStringDataAttribute = (value, fallback) => {
    if (typeof value !== 'string') return fallback;
    const normalized = value.trim();
    return normalized === '' ? fallback : normalized;
  };

  const getDeclarativeRootOptions = (root) => {
    if (!(root instanceof HTMLElement)) return {};
    const {
      sbThumbnailInitKey,
      sbThumbnailInitMedia,
      sbThumbnailBindAddToCart,
      sbThumbnailAtcBoundFlag,
      sbThumbnailAtcSource,
      sbThumbnailAtcSourceIdPrefix,
      sbThumbnailResetAddedClassMode,
    } = root.dataset;

    return {
      rootInitializedDataKey: parseStringDataAttribute(sbThumbnailInitKey, 'sbThumbnailSectionInitialized'),
      initMedia: parseBooleanDataAttribute(sbThumbnailInitMedia, true),
      bindAddToCart: parseBooleanDataAttribute(sbThumbnailBindAddToCart, true),
      addToCartBoundFlag: parseStringDataAttribute(sbThumbnailAtcBoundFlag, 'sbThumbnailAtcBound'),
      addToCartSource: parseStringDataAttribute(sbThumbnailAtcSource, 'product-thumbnail'),
      addToCartSourceIdPrefix: parseStringDataAttribute(
        sbThumbnailAtcSourceIdPrefix,
        'product-thumbnail'
      ),
      resetAddedClassMode: parseStringDataAttribute(sbThumbnailResetAddedClassMode, 'small-only'),
    };
  };

  const initDeclarativeSections = (scope = document) => {
    getMatchingRoots(scope, DECLARATIVE_ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      initRoot(root, getDeclarativeRootOptions(root));
    });
  };

  window.SBProductThumbnailSections = {
    bindAddToCartButtons,
    bindQuickAddModalTriggers,
    ensureThumbnailMediaScope,
    initDeclarativeSections,
    initRoot,
    initSection,
  };

  const initializeInitialDeclarativeSections = () => {
    initDeclarativeSections(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeInitialDeclarativeSections, { once: true });
  } else {
    initializeInitialDeclarativeSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event || !(event.target instanceof Element)) return;
    initDeclarativeSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    getMatchingRoots(event.target, '.sb-product-thumbnail__quick-add-modal').forEach(closeQuickAddModal);
  });

  if (typeof window.matchMedia === 'function') {
    const quickAddMedia = window.matchMedia(QUICK_ADD_DESKTOP_QUERY);
    const handleQuickAddViewportChange = () => {
      if (!quickAddMedia.matches) closeAllQuickAddModals();
    };

    if (typeof quickAddMedia.addEventListener === 'function') {
      quickAddMedia.addEventListener('change', handleQuickAddViewportChange);
    } else if (typeof quickAddMedia.addListener === 'function') {
      quickAddMedia.addListener(handleQuickAddViewportChange);
    }
  }

  document.dispatchEvent(new CustomEvent(SECTIONS_READY_EVENT));
})();
